
import type { UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useMemo } from "react";

import type { TechnicusCampaignIntegrationDto } from "./types";
import { useTechnicusCampaignIntegrations } from "./useTechnicusCampaignIntegrations";
import { registerCampaignsIntegrationDataHook } from "../../hooks/integrationDataHooks";
import { registerCampaignsItemsDataHook } from "../../hooks/itemsDataHooks";
import { registerCampaignEnrichment } from "../../hooks/useCampaignIntegrationsInfinite";

import type { ModuleDataValue } from "@/features/integrations/shared/renderer/hooks/types";
import { formatWhole, formatCurrency } from "@/utils/valueFormat";

registerCampaignsIntegrationDataHook("technicus", function useTechnicusCampaignsIntegrationData(params) {
  const { clientId } = params;
  const queryResult = useTechnicusCampaignIntegrations({ clientId });

  const transformedData = useMemo(() => {
    const summary = queryResult.data?.summary;
    if (!summary) return undefined;

    return {
      totalCampaigns: summary.totalCampaigns,
      activeOnTechnicus: summary.activeRemotely,
      syncedCount: summary.syncedCount,
      syncedLabel: summary.syncedCount,
      syncedSubtitle: `Of ${formatWhole(summary.totalCampaigns)} Technicus campaigns`,
    };
  }, [queryResult.data?.summary]);

  return {
    ...queryResult,
    data: transformedData,
  } as unknown as UseQueryResult<Record<string, ModuleDataValue>, AxiosError>;
});

registerCampaignsItemsDataHook("technicus", (clientId) =>
  clientId ? useTechnicusCampaignsItemsDataImpl : undefined
);

function useTechnicusCampaignsItemsDataImpl(params: {
  clientId: string | undefined;
  tenantId: string | undefined;
  page: number;
  pageSize: number;
  search?: string;
  filterValues?: Record<string, string>;
}) {
  const { clientId, page, pageSize, search, filterValues } = params;
  const statusFilter = filterValues?.statusFilter;
  const queryResult = useTechnicusCampaignIntegrations({ clientId, page, pageSize, search, statusFilter });

  const transformedData = useMemo(() => {
    if (!queryResult.data) return undefined;

    const enrichedItems = (queryResult.data.items ?? []).map((item: TechnicusCampaignIntegrationDto) => {
      const falconStatusBadge = item.falconStatus === "Active"
        ? { label: "common.active", variant: "Success" }
        : item.falconStatus === "Paused"
          ? { label: "common.paused", variant: "Warning" }
          : { label: "common.inactive", variant: "Default" };

      const syncStatusBadge = item.syncStatus === "Synced"
        ? { label: "integrations.technicus.campaigns.synced", variant: "Success" }
        : item.syncStatus === "NotSynced"
          ? { label: "integrations.technicus.campaigns.notSynced", variant: "Warning" }
          : { label: "integrations.technicus.campaigns.failed", variant: "Error" };

      const startDateFormatted = item.startDate ? new Date(item.startDate).toLocaleDateString() : "";
      const endDateFormatted = item.endDate ? new Date(item.endDate).toLocaleDateString() : "—";

      const budgetFormatted = item.monthlyBudget != null
        ? formatCurrency(item.monthlyBudget)
        : "";

      const currentMonthBudgetFormatted = item.currentMonthBudget != null
        ? formatCurrency(item.currentMonthBudget)
        : null;
      const remoteBudgetFormatted = item.remoteBudget != null
        ? formatCurrency(item.remoteBudget)
        : null;

      const remoteStatusLower = item.remoteStatus?.toLowerCase();
      const technicusStatusInline = item.syncStatus === "Failed"
        ? { label: "integrations.technicus.campaigns.issuesFound", variant: "warning" }
        : remoteStatusLower === "active"
          ? { label: "integrations.technicus.campaigns.technicusActive", variant: "success" }
          : remoteStatusLower === "paused"
            ? { label: "common.paused", variant: "warning" }
            : { label: "integrations.technicus.campaigns.technicusInactive", variant: "neutral" };

      const remoteStatusValue = remoteStatusLower === "active" ? "Active"
        : remoteStatusLower === "paused" ? "Paused"
          : "Inactive";

      return {
        ...item,
        falconStatusBadge,
        syncStatusBadge,
        startDateFormatted,
        endDateFormatted,
        budgetFormatted,
        currentMonthBudgetFormatted,
        remoteBudgetFormatted,
        technicusStatusInline,
        remoteStatusValue,
      };
    });

    return {
      data: enrichedItems,
      totalRecords: queryResult.data.totalRecords,
    };
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: transformedData,
  } as unknown as UseQueryResult<{ data: unknown[]; totalRecords: number }, AxiosError>;
}

registerCampaignEnrichment("technicus", (items) =>
  (items as TechnicusCampaignIntegrationDto[]).map((item) => {
    const falconStatusBadge = item.falconStatus === "Active"
      ? { label: "common.active", variant: "Success" }
      : item.falconStatus === "Paused"
        ? { label: "common.paused", variant: "Warning" }
        : { label: "common.inactive", variant: "Default" };

    const syncStatusBadge = item.syncStatus === "Synced"
      ? { label: "integrations.technicus.campaigns.synced", variant: "Success" }
      : item.syncStatus === "NotSynced"
        ? { label: "integrations.technicus.campaigns.notSynced", variant: "Warning" }
        : { label: "integrations.technicus.campaigns.failed", variant: "Error" };

    const startDateFormatted = item.startDate ? new Date(item.startDate).toLocaleDateString() : "";
    const endDateFormatted = item.endDate ? new Date(item.endDate).toLocaleDateString() : "—";

    const budgetFormatted = item.monthlyBudget != null
      ? formatCurrency(item.monthlyBudget)
      : "";

    const currentMonthBudgetFormatted = item.currentMonthBudget != null
      ? formatCurrency(item.currentMonthBudget)
      : null;
    const remoteBudgetFormatted = item.remoteBudget != null
      ? formatCurrency(item.remoteBudget)
      : null;

    const remoteStatusLower = item.remoteStatus?.toLowerCase();
    const technicusStatusInline = item.syncStatus === "Failed"
      ? { label: "integrations.technicus.campaigns.issuesFound", variant: "warning" }
      : remoteStatusLower === "active"
        ? { label: "integrations.technicus.campaigns.technicusActive", variant: "success" }
        : remoteStatusLower === "paused"
          ? { label: "common.paused", variant: "warning" }
          : { label: "integrations.technicus.campaigns.technicusInactive", variant: "neutral" };

    const remoteStatusValue = remoteStatusLower === "active" ? "Active"
      : remoteStatusLower === "paused" ? "Paused"
        : "Inactive";

    return {
      ...item,
      falconStatusBadge,
      syncStatusBadge,
      startDateFormatted,
      endDateFormatted,
      budgetFormatted,
      currentMonthBudgetFormatted,
      remoteBudgetFormatted,
      technicusStatusInline,
      remoteStatusValue,
    };
  }),
);

export { useTechnicusCampaignIntegrations } from "./useTechnicusCampaignIntegrations";
export type {
  TechnicusCampaignIntegrationDto,
  TechnicusCampaignIntegrationsResponse,
} from "./types";
